import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../../constants/fonts';

import RcaByCategoryHeatmapSection from '../../components/RcaByCategoryHeatmapSection';

import type {
  RcaMatrixRowData,
  RcaResolutionCategoryData,
  RcaSubCategoryMap,
  TicketingCategoriesPayload,
  TicketingRcaPayload,
} from '../../components/types';
import AnalyticsChartCard from '../../components/AnalyticsChartCard';

interface RcaProps {
  loading: boolean;
  error: string | null;
  data: TicketingRcaPayload | null;
  categoriesData: TicketingCategoriesPayload | null;
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const Rca: React.FC<RcaProps> = ({
  loading,
  error,
  data,
  categoriesData,
  onRetry,
  hasActiveFilters = false,
}) => {
  const resolutionRows = useMemo<RcaResolutionCategoryData[]>(() => {
    return (data?.resolutionByRcaCategory?.categories ?? []).map((item) => ({
      name: item.name,
      count: item.count,
      percentage: item.percentage,
    }));
  }, [data?.resolutionByRcaCategory?.categories]);

  const heatMatrixRows = useMemo<RcaMatrixRowData[]>(() => {
    return (data?.rcaByCategory?.matrix ?? []).map((item) => ({
      rcaCategory: item.rcaCategory,
      values: item.values,
    }));
  }, [data?.rcaByCategory?.matrix]);

  const heatSubCategoriesByCategory = useMemo<RcaSubCategoryMap>(() => {
    const categories =
      categoriesData?.categoryBreakdown?.total?.categories ?? [];

    return categories.reduce<RcaSubCategoryMap>((acc, category) => {
      acc[category.name] = (category.subCategories ?? []).map((sub) => ({
        name: sub.name,
        count: sub.count,
      }));

      return acc;
    }, {});
  }, [categoriesData?.categoryBreakdown?.total?.categories]);

  return (
    <View style={styles.content}>
    <AnalyticsChartCard
      title={
        data
          ?.resolutionByRcaCategory
          ?.title ??
        'Resolution by RCA Category'
      }
      description={
        data
          ?.resolutionByRcaCategory
          ?.description ??
        ''
      }
      chartType="horizontal-bar"
      rows={resolutionRows.map(
        item => ({
          label: item.name,
          value: item.count,
          percentage:
            item.percentage,
        }),
      )}
      loading={loading}
      error={error}
      onRetry={onRetry}
      hasActiveFilters={hasActiveFilters}
      skeletonRowCount={5}
    />

      <RcaByCategoryHeatmapSection
        title={data?.rcaByCategory?.title ?? 'RCA by Category'}
        description={data?.rcaByCategory?.description ?? ''}
        categories={data?.rcaByCategory?.categories ?? []}
        rcaCategories={data?.rcaByCategory?.rcaCategories ?? []}
        matrixRows={heatMatrixRows}
        subCategoriesByCategory={heatSubCategoriesByCategory}
        subCategoryRcaData={
          data?.rcaByCategory?.subCategoryMatrix?.reduce<Record<string, Record<string, Record<string, number>>>>(
            (acc, item) => {
              acc[item.rcaCategory] = item.categorySubCategoryValues;
              return acc;
            },
            {}
          )
        }
        apiSubCategoriesByCategory={data?.rcaByCategory?.subCategoriesByCategory}
        loading={loading}
        error={error}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingVertical: 16,
    paddingHorizontal: 0,
  },
  metaError: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    marginTop: 6,
    marginBottom: 8,
  },
});

export default Rca;